import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {getTransformationErrorText} from 'analysis-tool/ui/main-view/MainViewHelpers';
import {assert, assertAndContinue} from 'Asserts';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD, FONT_PROXIMA_15} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import DataViewContext, {useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';

export interface FailedViewWithMessageProps {
  statusMessage: string;
}

export type FailedViewProps = FailedViewWithMessageProps | Record<string, never>;

function isFailedViewWithMessage(props: FailedViewProps): props is FailedViewWithMessageProps {
  return props.hasOwnProperty('statusMessage');
}

/**
 * Render view for failed views.
 *
 * Can be used in two ways:
 * 1. With a `statusMessage` prop: Renders the error with the provided message.
 * 2. With no props: Uses analysisView from context to determine the error message.
 *
 * @example
 * // With status message
 * <FailedView statusMessage="Something went wrong" />
 *
 * // With analysisView from DataViewContext
 * <FailedView />
 */
export default function FailedView(props: FailedViewProps = {}): JSX.Element {
  if (isFailedViewWithMessage(props)) {
    return <FailedViewContent statusMessage={props.statusMessage} />;
  }

  return <FailedViewWithAnalysisView />;
}

export function FailedViewContent({statusMessage}: FailedViewWithMessageProps): JSX.Element {
  return (
    <DataViewPane>
      <div className={css(styles.errorBanner)}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={statusMessage}
          dataTestAttrs={{component: 'FailedView', element: 'errorBanner'}}
        />
      </div>
      <div className={css(styles.errorIconContent)}>
        <BenchlingIcon icon="warning" className={css(styles.icon)} />
        <div className={css(styles.errorText)}>Failed to render view</div>
      </div>
    </DataViewPane>
  );
}

function FailedViewWithAnalysisView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, previewData} = dataViewContextValue;

  assert(
    analysisView != null,
    'analysisView must be provided in context when using FailedViewWithAnalysisView'
  );

  const {lineage} = analysisView;
  let statusMessage = 'Failed to render view';
  if (previewData?.previewDraftStepData.errorMessage) {
    statusMessage = previewData.previewDraftStepData.errorMessage;
  } else {
    const firstFailedStep = lineage.find((node) => node.step.statusCode === PipelineStepStatusCode.FAILED);
    statusMessage =
      firstFailedStep?.step.id != null
        ? getTransformationErrorText(firstFailedStep.step.statusMessage, firstFailedStep.step.stepType)
        : statusMessage;
    if (statusMessage.includes('Ancestor step is FAILED.')) {
      // Strip out the preamble in the regular transformation error text for a clearer message.
      statusMessage = 'Ancestor step is FAILED.';
    }
    assertAndContinue(firstFailedStep != null, 'Expected at least one failed step to render FailedView');
  }

  const newDataViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      dataTestAttrs: {component: 'FailedView', element: 'root'},
    }),
    [dataViewContextValue]
  );

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <FailedViewContent statusMessage={statusMessage} />
    </DataViewContext.Provider>
  );
}

const styles = StyleSheet.create({
  icon: {
    color: GRAY_LIGHT,
    height: '60px',
    width: '60px',
  },
  errorBanner: {
    margin: 10,
  },
  errorIconContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    ...FONT_PROXIMA_15,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARK,
    marginTop: '10px',
  },
});
